/**
 * @-Mention Parser
 *
 * Pure, tested utilities for the chat composer's `@`-mention affordance.
 * Mirrors the sibling `command-parser.ts` precedent: a pure util drives a
 * chat-input affordance, with no React/DOM/network coupling so it is trivially
 * testable and reusable.
 *
 * A mention is the canonical inline marker `@[Title](type:id)` embedded directly
 * in the composer's plain-text string. The marker is string-embeddable so it
 * survives the current `<textarea>`-backed composer (string state) without a
 * contentEditable rewrite. `type`/`id`/`title` are keyed to `SearchResultObject`
 * (`@/lib/api/endpoints/search.ts`), so a future autocomplete dropdown — backed
 * by the existing `hybridSearch` endpoint — maps a result item straight into a
 * `MentionReference` with no new API.
 *
 * This util is the load-bearing foundation; the dropdown UI, keyboard-nav,
 * drag-drop, and agent ingestion are deliberately out of scope here.
 */

/**
 * A resolved reference to a workspace object, keyed to `SearchResultObject`
 * (`{ id, type, title }`) so a `hybridSearch` result maps straight in.
 */
export interface MentionReference {
  id: string;
  type: string;
  title: string;
}

/**
 * `dataTransfer` MIME for an object dragged from a module pane into the chat
 * input (issue #1457). The drag source sets this key to a JSON `MentionReference`;
 * the chat drop target reads it and splices the canonical marker in. A custom
 * vendor MIME (not `text/plain`) so a normal text/file drop never misfires.
 */
export const MENTION_DRAG_MIME = 'application/x-xavior-object';

/**
 * Parse a `MentionReference` out of a drag `dataTransfer`. Reads the vendor MIME
 * first, falling back to a `text/plain` JSON payload (some browsers normalize
 * custom types). Returns null when the drop carries no valid object reference
 * (e.g. a file drop) so the caller can defer to its file-drop path.
 */
export function parseDroppedReference(raw: string | null | undefined): MentionReference | null {
  if (!raw) return null;
  try {
    const obj = JSON.parse(raw) as Partial<MentionReference>;
    if (obj && typeof obj.id === 'string' && typeof obj.type === 'string') {
      return { id: obj.id, type: obj.type, title: typeof obj.title === 'string' ? obj.title : obj.id };
    }
  } catch {
    // not JSON — not an object reference
  }
  return null;
}

/**
 * The in-progress `@token` under the caret. `start`/`end` are string offsets
 * (the span from the `@` through the caret), so `applyMention` can splice the
 * canonical marker in and the textarea can restore its selection.
 */
export interface ActiveMention {
  query: string;
  start: number;
  end: number;
}

/**
 * Canonical inline marker: `@[Title](type:id)`.
 *
 * - Title: any run of non-`]` chars (sanitized on insert so `]`/`)` never appear).
 * - Type: a word starting with `[A-Za-z0-9_]`, then word chars or `-`
 *   (matches `SearchResultObject.type` — contact/task/event/note/place/doc/asset).
 * - Id: any run of non-`)` chars.
 *
 * NOTE: exported WITHOUT the `/g` flag. A `/g` regex carries mutable `lastIndex`
 * state, so a shared singleton would leak that state across callers (consecutive
 * `.test()` calls alternate true/false; consecutive `.exec()` calls skip matches).
 * The two stateful-iterator call sites below each clone it with `'g'` added.
 */
export const MENTION_MARKER_RE = /@\[([^\]]+)\]\((\w[\w-]*):([^)]+)\)/;

/** A char that may precede an `@` for it to trigger a mention. */
function isBoundaryChar(ch: string): boolean {
  // whitespace or "(" — so `email a@b.com` does NOT trigger (preceded by a letter).
  return /\s/.test(ch) || ch === '(';
}

/**
 * Detect an in-progress `@token` ending at the caret. The per-keystroke trigger
 * the autocomplete dropdown will call.
 *
 * Rules:
 * - The `@` must be at string start or preceded by whitespace/`(` (so emails like
 *   `a@b.com` do not trigger).
 * - The `@` must not be part of `@@` (escape — matches the internal
 *   `@@XAVIOR_RESULT_CARDS@@` sentinel convention so sentinels never trigger).
 * - The `@` must not open an already-applied marker `@[Title](type:id)` — a caret
 *   inside a resolved marker must not re-open autocomplete with a `[`-prefixed query.
 * - The query span (from `@`+1 to the caret) must contain no whitespace.
 *
 * @returns `{ query, start, end }` for the active mention, or `null`.
 */
export function detectActiveMention(text: string, caret: number): ActiveMention | null {
  if (caret < 1 || caret > text.length) return null;

  // Scan backwards from the caret to the nearest `@`, bailing on whitespace.
  let at = -1;
  for (let i = caret - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === '@') {
      at = i;
      break;
    }
    if (/\s/.test(ch)) return null; // whitespace inside the token ⇒ not a mention
  }
  if (at === -1) return null;

  // `@` must be at string start or after a boundary char.
  if (at > 0 && !isBoundaryChar(text[at - 1])) return null;

  // `@@` escape / sentinel: the char after `@` is another `@`.
  if (text[at + 1] === '@') return null;

  // Already-applied marker `@[Title](type:id)`: a caret inside a resolved marker
  // must not re-open autocomplete with a bogus `[`-prefixed query.
  if (text[at + 1] === '[') return null;

  return {
    query: text.slice(at + 1, caret),
    start: at,
    end: caret,
  };
}

/** Strip chars that would make the marker ambiguous. */
function sanitizeTitle(title: string): string {
  return title.replace(/[\])]/g, '').trim();
}

/**
 * Replace the active `@token` with the canonical marker `@[Title](type:id)` plus
 * a trailing space, returning the new text and the caret position after it (so
 * the textarea can restore selection).
 */
export function applyMention(
  text: string,
  active: ActiveMention,
  ref: MentionReference
): { text: string; caret: number } {
  const marker = `@[${sanitizeTitle(ref.title)}](${ref.type}:${ref.id})`;
  const insert = `${marker} `;
  const next = text.slice(0, active.start) + insert + text.slice(active.end);
  return { text: next, caret: active.start + insert.length };
}

/**
 * Extract structured references from composed text by running the marker regex.
 * De-duped by id (first occurrence wins). The seam that later passes referenced
 * objects to the agent as structured context.
 */
export function extractMentionReferences(text: string): MentionReference[] {
  const refs: MentionReference[] = [];
  const seen = new Set<string>();
  // Fresh regex (the exported one is /g and stateful) — clone to stay reentrant.
  const re = new RegExp(MENTION_MARKER_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const [, title, type, id] = m;
    if (seen.has(id)) continue;
    seen.add(id);
    refs.push({ id, type, title });
  }
  return refs;
}

/**
 * Replace each marker with its plain `@Title` for display/fallback rendering.
 */
export function stripMentionMarkers(text: string): string {
  const re = new RegExp(MENTION_MARKER_RE.source, 'g');
  return text.replace(re, (_full, title: string) => `@${title}`);
}
